import { branchValuesSorted, conditionValuesSorted, datatypeOperatorValuesList, datatypeOperatorValuesMap, Expression, ExpressionCondition, ExpressionField, ExpressionTokenType, SchemaFieldType } from './TypeDefs';

const extract = (field: SchemaFieldType, prefix: string[], result: ExpressionField[]) => {
  const fieldName = field.name;
  const fieldType = field.type;

  if (Array.isArray(fieldType)) {
    if (fieldType.reduce((prevValue, type) => prevValue && typeof type === 'string', true)) {
      result.push({
        field: [...prefix, fieldName].join('.'),
        type: (fieldType as string[]).map((f) => f.toLowerCase()).find((f) => f !== 'null') ?? ''
      });
    } else {
      result.push(...extractPayloadFields(fieldType as SchemaFieldType[], [...prefix, fieldName]));
    }
  } else if (fieldType === 'array') {
    result.push({ field: prefix.join('.'), type: 'array' });
  } else if (typeof fieldType === 'string' && fieldType !== 'record') {
    result.push({ field: prefix.join('.'), type: fieldType });
  } else if (field.fields && Array.isArray(field.fields)) {
    result.push(...extractPayloadFields(field.fields, prefix));
  } else if (fieldType && (fieldType as SchemaFieldType).fields && Array.isArray((fieldType as SchemaFieldType).fields)) {
    result.push(...extractPayloadFields((fieldType as SchemaFieldType).fields as SchemaFieldType[], [...prefix, fieldName]));
  } else if (fieldType && (fieldType as SchemaFieldType).type !== 'record') {
    result.push(...extractPayloadFields(fieldType as SchemaFieldType, [...prefix, fieldName]));
  }
};

/**
  * @method extractPayloadFields
  * @description extracts the nested payload field from an object with prefix attached to them
  * @param fields
  * @param prefix
  * @returns result array containing field names
  */
export const extractPayloadFields = (fields: SchemaFieldType[] | SchemaFieldType, prefix: string[] = []): ExpressionField[] => {
  const result: ExpressionField[] = [];
  if (Array.isArray(fields)) {
    fields.forEach((field: SchemaFieldType) => {
      extract(field, prefix, result);
    });
  } else if (fields) {
    extract(fields, prefix, result);
  }
  return result;
};

/**
 * @method generateExpression
 * @description generates raw expression based on the inputs provided
 * @param expression
 * @param fieldType
 * @returns expression string
 */
export const generateExpression = (expression: Expression, fieldType: { [key: string]: string }) => {
  let exprStr = '';
  const fields = Object.keys(fieldType);
  for (let i = 0; i < expression.length; i += 1) {
    if (i < expression.length - 1 && expression[i].type === ExpressionTokenType.OPERATOR && expression[i - 1].type === ExpressionTokenType.FIELD && expression[i + 1].type === ExpressionTokenType.VALUE) {
      const fieldToken = expression[i - 1];
      const operatorToken = expression[i];
      const valueToken = expression[i + 1];

      const type = fieldType[fieldToken.value];
      const expr = (datatypeOperatorValuesMap[type] ?? datatypeOperatorValuesMap.array)[operatorToken.value];

      let { value } = valueToken;
      if (fields.includes(value)) {
        value = `payload.${value}`;
      } else if (!value.startsWith('\'')) {
        value = `'${value}'`;
      }
      exprStr += `${expr.replace('{{field}}', fieldToken.value).replace('{{value}}', value)}`;
    } else if (expression[i].type === ExpressionTokenType.CONDITION) {
      const condValue = expression[i].value;
      if (condValue === ExpressionCondition.AND) {
        exprStr += ' && ';
      } else if (condValue === ExpressionCondition.OR) {
        exprStr += ' || ';
      }
    }
  }
  return exprStr ? `\${(${exprStr})}` : '';
};

/**
 * @method getValueTokenTypeData
 * @description looks for nearBy first  space, ' or ""
 * @param inputData
 * @returns inputData values
 */
export const getValueTokenTypeData = (inputData: string) => {
  if (inputData.startsWith('\'')) {
    // look for nearby '
    const nearByQuoteIdx = inputData.indexOf('\'', 1);
    if (nearByQuoteIdx <= 0) {
      return undefined;
    }
    return inputData.substring(0, nearByQuoteIdx + 1);
  }
  if (inputData.startsWith('"')) {
    // look for nearby "
    const nearByQuoteIdx = inputData.indexOf('"', 1);
    if (nearByQuoteIdx <= 0) {
      return undefined;
    }
    // replace " with '
    return inputData.substring(0, nearByQuoteIdx + 1).replaceAll('"', '\'');
  }
  // look for first Space
  let nearBySpaceIdx = inputData.indexOf(' ');
  if (nearBySpaceIdx < 0) {
    nearBySpaceIdx = inputData.length;
  }
  return inputData.substring(0, nearBySpaceIdx);
};

/**
 * @method getTokenData
 * @description segregates FIELD, OPERATOR, VALUE and CONDITION from the input expression
 * @param param0
 * @returns { nextTokenType, tokenValue }
 */
export const getTokenData = ({
  inputData,
  currTokenType,
  fields,
  operatorValues
}: {
  inputData: string,
  currTokenType?: ExpressionTokenType,
  fields: string[],
  operatorValues: string[]
}) => {
  const inputDataLower = inputData.toLowerCase();
  let nextTokenType = currTokenType;
  let tokenValue: string | undefined;

  if (currTokenType === ExpressionTokenType.BRANCH) {
    tokenValue = branchValuesSorted.find((v) => inputDataLower.startsWith(v.toLowerCase()));
    nextTokenType = ExpressionTokenType.FIELD;
  } else if (currTokenType === ExpressionTokenType.FIELD) {
    nextTokenType = ExpressionTokenType.OPERATOR;
    tokenValue = fields.find((v) => inputData.startsWith(v));
  } else if (currTokenType === ExpressionTokenType.OPERATOR) {
    nextTokenType = ExpressionTokenType.VALUE;
    tokenValue = [...operatorValues].sort((a, b) => b.length - a.length).find((v) => inputDataLower.startsWith(v.toLowerCase()));
  } else if (currTokenType === ExpressionTokenType.VALUE) {
    tokenValue = getValueTokenTypeData(inputData);
  } else if (currTokenType === ExpressionTokenType.CONDITION) {
    tokenValue = conditionValuesSorted.find((v) => inputDataLower.startsWith(v.toLowerCase()));
    nextTokenType = ExpressionTokenType.FIELD;
  }

  if (!tokenValue) {
    return undefined;
  }

  if (currTokenType === ExpressionTokenType.VALUE) {
    if (tokenValue.length > 0 && inputData.length > tokenValue.length && inputData[tokenValue.length] === ' ') {
      nextTokenType = ExpressionTokenType.CONDITION;
    }
  } else if (currTokenType === ExpressionTokenType.FIELD) {
    // get till the nearby space, in case working with array
    const nearBySpace = inputData.indexOf(' ');
    if (nearBySpace > 0) {
      tokenValue = inputData.substring(0, nearBySpace);
    }
  }

  return { nextTokenType, tokenValue };
};

/**
 * @method prepareExpressionFromStr
 * @description prepares the expression from the input string by setting the list of operations on field and update the expression with actual token value
 * @param param0
 * @returns { expText, expression, nextTokenType, currTokenType, operatorValues, lastTokenVal: lastTokenVal.split(' ')[0], processedCount }
 */
export const prepareExpressionFromStr = ({
  text,
  fieldTypes,
  fields,
}: {
  text: string,
  fields: string[],
  fieldTypes: { [key: string]: string }
}) => {
  const expression: Expression = [];
  let expText = text;

  let nextTokenType: ExpressionTokenType | undefined = ExpressionTokenType.BRANCH;
  let currTokenType: ExpressionTokenType | undefined = ExpressionTokenType.BRANCH;
  let processedCount = 0;
  let operatorValues: string[] = [];
  let lastTokenVal = '';

  while (nextTokenType !== undefined && processedCount < expText.length) {
    let inputData = expText.substring(processedCount);
    const inputDataLen = inputData.length;
    inputData = inputData.trimStart();
    const spacesAtStart = inputDataLen - inputData.length;
    currTokenType = nextTokenType;

    lastTokenVal = inputData;

    const tokenData = getTokenData({
      currTokenType,
      fields,
      operatorValues,
      inputData,
    });

    if (!tokenData) {
      break;
    }

    const { tokenValue } = tokenData;
    nextTokenType = tokenData.nextTokenType;

    if (currTokenType === ExpressionTokenType.FIELD) {
      // set the list of operators based on field
      operatorValues = datatypeOperatorValuesList[fieldTypes[tokenValue]] ?? datatypeOperatorValuesList.array;
    }
    if (![ExpressionTokenType.VALUE, ExpressionTokenType.FIELD].includes(currTokenType)) {
      const processedCountWithSpaces = processedCount + spacesAtStart;
      // update the expression with actual token value if found, eg. if with IF
      expText = expText.substring(0, processedCountWithSpaces) + tokenValue + expText.substring(processedCountWithSpaces + tokenValue.length);
    }

    processedCount += tokenValue.length + spacesAtStart;
    if (tokenValue) {
      expression.push({
        type: currTokenType,
        value: tokenValue,
      });
    }
  }

  return { expText, expression, nextTokenType, currTokenType, operatorValues, lastTokenVal: lastTokenVal.split(' ')[0], processedCount };
};

export const newLineRegex = /\r?\n|\r/g; // matches CRLF, LF, or CR line ending characters
